import { trace } from "@opentelemetry/api";
import * as turf from "@turf/turf";
import type { FeatureCollection, Point } from "geojson";

import { createLogger } from "@/logger";

const log = createLogger("feed");

import { idx, rowsByIndex, rowsWhere, store } from "@/store/store";
import { MembershipAvailability } from "@/store/types";

export async function lookupWorkspaceMembershipsWithinRadius(
  latitude: number,
  longitude: number,
  radius: number,
  units: string,
  attributes?: Record<string, string>,
): Promise<{ id: string; title: string }[]> {
  if (!attributes?.workspaceId || !attributes?.workspaceMembershipId) {
    return Promise.reject("workspaceId and workspaceMembershipId are required in attributes");
  }

  const span = trace.getTracer("geoLookup").startSpan("lookupWorkspaceMembershipsWithinRadius");
  span.setAttributes(attributes);
  span.setAttributes({
    latitude,
    longitude,
    radius,
  });

  const turfUnits: turf.Units = units as turf.Units;
  const startTime = performance.now();
  const center = [longitude, latitude];
  const options = { steps: 10, units: turfUnits };
  const circle = turf.circle(center, radius, options);

  const twelveHoursAgo = new Date(Date.now() - 1000 * 60 * 60 * 12);

  const cutoff = twelveHoursAgo.toISOString();
  const members = rowsWhere(
    "wsMembershipAvailability",
    (availability) =>
      availability.mostRecentDeviceLocationLatitude != null &&
      availability.mostRecentDeviceLocationLongitude != null &&
      availability.mostRecentDeviceLocationUpdatedAt != null &&
      availability.mostRecentDeviceLocationUpdatedAt >= cutoff,
  );

  const memberQueryEndTime = performance.now();
  const memberPoints = generateFeaturesFromMembers(members);
  const geoCalculationStartTime = performance.now();
  const membersWithinRadius = turf.pointsWithinPolygon(memberPoints, circle);
  const geoCalculationEndTime = performance.now();

  const commandAliasQueryStartTime = performance.now();
  const membershipIdsWithinRadius = new Set(
    membersWithinRadius.features.map((feature) => feature.properties?.workspaceMembershipId),
  );
  const commandAliasChannels = rowsWhere(
    "workspaceCommandAlias",
    (alias) => alias.alias === "dispatch" && membershipIdsWithinRadius.has(alias.workspaceMembershipId),
  );

  const commandAliasQueryEndTime = performance.now();
  const foundIds = commandAliasChannels?.map((channel) => channel.feedId) ?? [];

  const feeds: { id: string; title: string | null }[] = [];
  for (const feedId of foundIds) {
    const feed = store.get("feed", feedId);
    if (!feed || feed.workspaceId !== attributes.workspaceId) continue;
    const hasWrite = rowsByIndex("permission", idx.permissionsByFeed, feed.id).some(
      (p) => p.name === "write" && p.enabled && p.workspaceMembershipId === attributes.workspaceMembershipId,
    );
    if (hasWrite) feeds.push({ id: feed.id, title: feed.title });
  }

  const overallEndTime = performance.now();

  span.setAttributes({
    numberOfMembers: members.length,
    numberOfMembersWithinRadius: membersWithinRadius.features.length,
    memberQueryTimeMs: memberQueryEndTime - startTime,
    geoCalculationTimeMs: geoCalculationEndTime - geoCalculationStartTime,
    commandAliasQueryTimeMs: commandAliasQueryEndTime - commandAliasQueryStartTime,
    overallTimeMs: overallEndTime - startTime,
  });
  span.end();

  log.debug("GEO", {
    numberOfMembers: members.length,
    numberOfMembersWithinRadius: membersWithinRadius.features.length,
    memberQueryTimeMs: memberQueryEndTime - startTime,
    geoCalculationTimeMs: geoCalculationEndTime - geoCalculationStartTime,
    commandAliasQueryTimeMs: commandAliasQueryEndTime - commandAliasQueryStartTime,
    overallTimeMs: overallEndTime - startTime,
  });

  return feeds?.map((feed) => ({ id: feed.id, title: feed.title ?? "" }));
}

function generateFeaturesFromMembers(
  members: MembershipAvailability[],
): FeatureCollection<Point, { workspaceMembershipId: string }> {
  return {
    type: "FeatureCollection",
    features: members.map((member) => ({
      type: "Feature",
      geometry: {
        type: "Point",
        coordinates: [member.mostRecentDeviceLocationLongitude, member.mostRecentDeviceLocationLatitude],
      },
      properties: {
        workspaceMembershipId: member.workspaceMembershipId,
      },
    })),
  };
}

// Feel free to delete, useful for performance testing
// function generateFakeLatLongs(count: number, near: [number, number]): FeatureCollection<Point> {
//   return {
//     type: "FeatureCollection",
//     features: Array.from({ length: count }, () => ({
//       type: "Feature",
//       geometry: {
//         type: "Point",
//         coordinates: fakeLatLongNear(near, 10),
//       },
//       properties: {},
//     })),
//   };
// }

// function fakeLatLongNear(near: [number, number], radius: number): [number, number] {
//   const [longitude, latitude] = near;
//   return [longitude + (Math.random() * 2 - 1) * radius, latitude + (Math.random() * 2 - 1) * radius];
// }
